<template>
  <div>
    <bs-page-title :titleLabel="fromParentTitle" :btnLabel="''"></bs-page-title>
    <bs-msg-alert :alertClass="alert.class">{{ alert.message }}</bs-msg-alert>

    <!-- タブ切り替え -->
    <MasterTabs :currentTab="currentTab" @tab-change="handleTabChange" />

    <!-- マスタ一覧タブ -->
    <div v-if="currentTab === 'list'">
      <MasterSearchForm
        :yearOptions="yearOptions"
        :masterTypeOptions="state.masterTypes"
        :selectedYear="state.selectedYear"
        :selectedMasterType="state.selectedMasterType"
        :loading="state.loading"
        @update:selectedYear="state.selectedYear = $event"
        @update:selectedMasterType="state.selectedMasterType = $event"
        @search="searchMasterData"
        @clear="clearData"
      />

      <MasterDataTable
        :masterData="sortedMasterData"
        :isSearched="state.isSearched"
        @edit="handleEdit"
        @delete="handleDelete"
      />
    </div>

    <!-- 新規作成タブ -->
    <div v-if="currentTab === 'create'">
      <MasterCreateForm
        :masterTypeOptions="state.masterTypes"
        @created="handleCreated"
        @cancel="handleCreateCancel"
        @alert="handleAlert"
      />
    </div>

    <!-- CSVインポートタブ -->
    <div v-if="currentTab === 'import'">
      <CsvImportForm
        :csvImportOptions="csvImportState.csvImportMasterOptions"
        :selectedMasterType="csvImportState.state.selectedMasterType"
        :previewData="csvImportState.state.previewData"
        :canImport="csvImportState.state.canImport"
        :totalPages="Math.max(1, Math.ceil(csvImportState.state.previewData.length / 50))"
        :currentPage="csvImportState.state.currentPage"
        :pagesToShow="Array.from({ length: Math.max(1, Math.ceil(csvImportState.state.previewData.length / 50)) }, (_, i) => i + 1)"
        :hasFileSelected="!!csvImportState.state.selectedFile"
        @update:selectedMasterType="csvImportState.state.selectedMasterType = $event"
        @file-upload="handleFileUpload"
        @preview="handlePreview"
        @import="handleImport"
        @clear="handleClearImport"
        @page-change="csvImportState.goToPage"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from 'vue'
import BsPageTitle from '../../molecules/BsPageTitle.vue'
import BsMsgAlert from '../../atoms/BsMsgAlert.vue'
import MasterTabs from './components/MasterTabs.vue'
import MasterSearchForm from './components/MasterSearchForm.vue'
import MasterDataTable from './components/MasterDataTable.vue'
import MasterCreateForm from './components/MasterCreateForm.vue'
import CsvImportForm from './components/CsvImportForm.vue'
import { useMasterData } from '@/composables/useMasterData'
import { useCsvImport } from '@/composables/useCsvImport'
import { MasterApiService } from '@/services/MasterApiService'
import { 
  inferMasterType, 
  getItemId, 
  getMasterTypeValue 
} from '@/utils/masterHelpers'

export default {
  name: 'MasterListView',
  components: {
    BsPageTitle,
    BsMsgAlert,
    MasterTabs,
    MasterSearchForm,
    MasterDataTable,
    MasterCreateForm,
    CsvImportForm
  },
  setup() {
    // Vue 3でrouterにアクセス
    const instance = getCurrentInstance()
    const router = instance?.appContext.config.globalProperties.$router
    
    // composablesの初期化
    const masterDataComposable = useMasterData()
    const csvImportComposable = useCsvImport()
    
    // 画面状態
    const fromParentTitle = 'マスタ管理'
    const currentTab = ref('list')

    // 分割代入で状態とメソッドを取得
    const {
      state,
      alert,
      yearOptions,
      sortedMasterData,
      initialize,
      searchMasterData,
      clearData,
      setAlert
    } = masterDataComposable

    // CSV機能のエイリアス
    const csvImportState = csvImportComposable

    // タブ切り替え処理
    const handleTabChange = (tab) => {
      currentTab.value = tab
      csvImportState.clearImport()
      
      if (tab === 'list') {
        setAlert('マスタ項目を選択して検索するか、空の状態で検索して全項目を表示してください。')
      } else if (tab === 'create') {
        setAlert('新しいマスタデータを作成できます。マスタタイプを選択してフォームに入力してください。')
      } else if (tab === 'import') {
        setAlert('CSVファイルを選択して、マスタデータをインポートしてください。')
      }
    }

    // 新規作成完了処理
    const handleCreated = (createdData) => {
      setAlert('マスタデータを作成しました。一覧タブで確認できます。', 'alert-success')
      // 一覧タブに切り替え
      setTimeout(() => {
        currentTab.value = 'list'
        // 作成されたデータを含む一覧を再取得
        searchMasterData()
      }, 1500)
    }

    // 新規作成キャンセル処理
    const handleCreateCancel = () => {
      currentTab.value = 'list'
      setAlert('新規作成をキャンセルしました。')
    }

    // アラート処理（子コンポーネントから）
    const handleAlert = (alertData) => {
      setAlert(alertData.message, alertData.class)
    }

    // 編集処理
    const handleEdit = (item) => {
      try {
        // マスタタイプの推定
        let masterType = item._master_type || inferMasterType(item)
        
        if (!masterType && state.selectedMasterType) {
          masterType = getMasterTypeValue(state.selectedMasterType, state.masterTypes)
        }

        if (!masterType) {
          setAlert('マスタタイプが選択されていません。マスタ項目を選択してから再度お試しください。', 'alert-danger')
          return
        }

        // ID取得
        const id = getItemId(item, masterType)
        if (!id) {
          setAlert('IDが取得できません。データ不整合の可能性があります。', 'alert-danger')
          return
        }

        // localStorageにデータ保存
        localStorage.setItem('data', JSON.stringify(item))
        
        // 編集画面への遷移
        const route = `/master/${masterType}/${id}`
        if (router && router.push) {
          router.push(route)
        } else {
          // フォールバック: 直接location変更
          window.location.href = `#${route}`
        }
      } catch (error) {
        console.error('編集処理エラー:', error)
        setAlert('編集処理中にエラーが発生しました。', 'alert-danger')
      }
    }

    // 削除処理
    const handleDelete = async (item) => {
      if (!confirm('このデータを削除してもよろしいですか？')) {
        return
      }

      try {
        // マスタタイプとIDの取得
        let masterType = item._master_type || inferMasterType(item)
        
        if (!masterType && state.selectedMasterType) {
          masterType = getMasterTypeValue(state.selectedMasterType, state.masterTypes)
        }

        if (!masterType) {
          setAlert('マスタタイプが選択されていません。', 'alert-danger')
          return
        }

        const id = getItemId(item, masterType)
        if (!id) {
          setAlert('IDが取得できません。', 'alert-danger')
          return
        }

        // API呼び出し
        const result = await MasterApiService.deleteMasterItem(masterType, id)
        
        if (result.success) {
          setAlert('データを削除しました', 'alert-success')
          await searchMasterData()
        } else {
          setAlert(result.message, 'alert-danger')
        }
      } catch (error) {
        console.error('削除処理エラー:', error)
        setAlert('データの削除に失敗しました', 'alert-danger')
      }
    }

    // CSVファイル選択処理
    const handleFileUpload = (event) => {
      const result = csvImportState.handleFileUpload(event)
      if (result.success) {
        setAlert(result.message)
      } else {
        setAlert(result.message, 'alert-danger')
      }
    }

    // CSVプレビュー処理
    const handlePreview = async () => {
      try {
        const result = await csvImportState.previewCsv()
        if (result.success) {
          setAlert(result.message)
        } else {
          setAlert(result.message, 'alert-danger')
        }
      } catch (error) {
        console.error('プレビューエラー:', error)
        setAlert(error.message || 'プレビュー処理でエラーが発生しました', 'alert-danger')
      }
    }

    // CSVインポート実行処理
    const handleImport = async () => {
      try {
        setAlert('インポート中...', 'alert-info')
        
        const result = await csvImportState.importCsv()
        if (result.success) {
          setAlert(result.message, 'alert-success')
          // インポート成功後、一覧タブに切り替えて再検索
          setTimeout(() => {
            currentTab.value = 'list'
            searchMasterData()
          }, 2000)
        } else {
          setAlert(result.message, 'alert-danger')
        }
      } catch (error) {
        console.error('インポートエラー:', error)
        setAlert(error.message || 'インポート処理でエラーが発生しました', 'alert-danger')
      }
    }

    // CSVインポートクリア処理
    const handleClearImport = () => {
      csvImportState.clearImport()
      setAlert('インポートデータをクリアしました。')
    }

    // 初期化
    onMounted(() => {
      initialize()
    })

    return {
      fromParentTitle,
      currentTab,
      state,
      alert,
      yearOptions,
      sortedMasterData,
      csvImportState,
      handleTabChange,
      handleCreated,
      handleCreateCancel,
      handleAlert,
      handleEdit,
      handleDelete,
      handleFileUpload,
      handlePreview,
      handleImport,
      handleClearImport,
      searchMasterData,
      clearData
    }
  }
}
</script>

<style scoped>
.nav-tabs .nav-link {
  cursor: pointer;
}
.btn-light.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
}
.btn-light.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}
</style>